import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { X, Download, Smartphone } from 'lucide-react';

export default function InstallPWA() {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Check if dismissed recently
    const dismissed = localStorage.getItem('pwa-prompt-dismissed');
    if (dismissed) {
      const dismissedTime = parseInt(dismissed, 10);
      // Don't show for 7 days after dismissal
      if (Date.now() - dismissedTime < 7 * 24 * 60 * 60 * 1000) {
        return;
      }
    }

    // Detect iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(isIOSDevice);

    // Listen for the beforeinstallprompt event (Chrome, Edge, etc.)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show prompt after a delay
      setTimeout(() => setShowPrompt(true), 3000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // For iOS, show prompt after delay if not installed
    if (isIOSDevice && !window.navigator.standalone) {
      setTimeout(() => setShowPrompt(true), 5000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      console.log('Install prompt outcome:', outcome);
      setDeferredPrompt(null);
    }
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    localStorage.setItem('pwa-prompt-dismissed', Date.now().toString());
    setShowPrompt(false);
  };

  if (!showPrompt || isInstalled) return null;

  return (
    <div 
      className="fixed bottom-20 left-4 right-4 z-50 animate-in slide-in-from-bottom-5 duration-300"
      data-testid="install-pwa-prompt"
    >
      <div className="bg-[#0a0a0a] border border-[#262626] rounded-2xl p-4 shadow-2xl max-w-md mx-auto">
        <button
          onClick={handleDismiss}
          className="absolute top-3 right-3 p-1 text-gray-500 hover:text-white rounded-lg hover:bg-white/10 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-xl bg-[#00FF94]/10 border border-[#00FF94]/20 flex items-center justify-center flex-shrink-0">
            <Smartphone className="w-7 h-7 text-[#00FF94]" />
          </div>
          
          <div className="flex-1 pr-6">
            <h3 className="font-bold text-white text-lg mb-1">
              Install PrivacyShield
            </h3>
            <p className="text-sm text-gray-400 mb-3">
              {isIOS 
                ? "Add to your home screen for quick access and a better experience."
                : "Install our app for quick access and offline support."
              }
            </p>

            {isIOS ? (
              <div className="bg-[#171717] rounded-lg p-3 text-sm text-gray-300">
                <p className="mb-2">To install:</p>
                <ol className="list-decimal list-inside space-y-1 text-gray-400">
                  <li>Tap the <span className="text-white">Share</span> button</li>
                  <li>Scroll and tap <span className="text-white">"Add to Home Screen"</span></li>
                  <li>Tap <span className="text-white">Add</span></li>
                </ol>
              </div>
            ) : (
              <Button
                onClick={handleInstall}
                className="btn-primary w-full"
              >
                <Download className="w-4 h-4 mr-2" />
                Install App
              </Button>
            )}
          </div>
        </div>

        <p className="text-xs text-gray-600 mt-3 text-center">
          No app store needed • Works on Android & iOS
        </p>
      </div>
    </div>
  );
}
